// Companies Список компаний
import React, { useMemo, useEffect, useCallback, useState } from "react";
import { Col, Row, Table, Card, Modal, Select, Input, Form, Button } from 'antd';
import { Toaster } from 'react-hot-toast'; // notifications
import { useToggle, useSelectedCompanyAddress, useCompanyFields } from 'hooks';
import ArchiSelect from 'components/Common/ArchiSelect';

// Zustand store
import { useShallow } from 'zustand/react/shallow';
import { useCompaniesStore, useUserStore } from 'storeZustand';

import Loader from 'components/Common/Loader';
import EditIcon from 'components/Common/Icons/EditIcon';
import StarIcon from 'components/Common/Icons/StarIcon';
import { PrimaryBtn } from 'components';
import CustomRadioCompanies from 'pages/Outstaffing/customRadioCompanies';

const Companies = () => {
	document.title = "Companies | Archi-crm";

	const [modal, toggleModal] = useToggle(false);
	const [isEdit, setIsEdit] = useState(false);
	const [selectedTypeCompany, setSelectedTypeCompany] = useState('Собственная');

	// Данные пользователя
	const isSuperUser = useUserStore(useShallow((state) => state.isSuperUser));

	// Все компании
	const [
		companies,
		loading,
		getCompanies,
		getCompanyById,
		patchCompany,
		singleCompany, // получение компании при редактировании
		addCompany
	] = useCompaniesStore(useShallow((state) => [
		state.companies,
		state.loading,
		state.getCompanies,
		state.getCompanyById,
		state.patchCompany,
		state.singleCompany,
		state.addCompany
	]));

	// получить список адресов компаний
	useEffect(() => {
		getCompanies();
	}, []);

	// Тип компании при редактировании
	useEffect(() => {
		if (singleCompany) {
			setSelectedTypeCompany(singleCompany?.company_type);
		}
	}, [singleCompany]);

	const [form] = Form.useForm();
	const fields = useCompanyFields(singleCompany);

	const {
		companyAddressData, selectedCompanyAddress, handleSelectedCompanyAddress, companyFactAddressData, selectedFactAddress, handleSelectedFactAddress, companyAddress12Data, selectedAddress12, handleSelectedAddress12, companyAddress34Data, selectedAddress34, handleSelectedAddress34, companyFilingAddressData, selectedFilingAddress, handleSelectedFilingAddress
	} = useSelectedCompanyAddress(singleCompany.address);

	const onFinish = async (values) => {
		const id = isEdit ? singleCompany.id : undefined;

		const companyData = {
			name: values.name,
			label: values.label,
			long_main_name: values.long_main_name,
			long_name: values.long_name,
			city: values.city,
			phone: values.phone,
			director: values.director,
			director_full_fio: values.director_full_fio,
			director_full_fio_rod_padeg: values.director_full_fio_rod_padeg,
			director_full_fio_dat_padeg: values.director_full_fio_dat_padeg,
			buhgalter: values.buhgalter,
			inn: values.inn,
			kpp: values.kpp || 0,
			ogrn: values.ogrn,
			bik: values.bik,
			bank_name: values.bank_name,
			bank_account_number: values.bank_account_number,
			bank_account_number_corporate: values.bank_account_number_corporate,
			okved: values.okved,
			company_type: selectedTypeCompany, // тип компании
			address: [
				selectedCompanyAddress?.value,
				selectedFactAddress?.value,
				selectedAddress12?.value,
				selectedAddress34?.value,
				selectedFilingAddress?.value]
				.filter((value) => value !== null && value !== undefined),
		};

		if (isEdit) {
			await patchCompany({ id, companyData });
		} else {
			await addCompany(companyData);
		}

		toggleModal();
	};

	const cancelFormFields = () => {
		toggleModal();
	}

	// Информация о конкретной компании
	const handleCellClick = useCallback((id) => {
		getCompanyById(id);
		setIsEdit(true);
		toggleModal();
	}, []);

	// Добавить компанию
	const handleAddCompany = () => {
		setIsEdit(false);
		toggleModal();
	}

	const handleTypeCompanyChange = (value) => {
		setSelectedTypeCompany(value);
	};

	const columns = useMemo(() => [
		{
			title: "Тип",
			dataIndex: "company_type",
			key: 'company_type',
		},
		{
			title: "Имя",
			dataIndex: "name",
			key: 'name',
		},
		{
			title: "Юр. Адрес",
			dataIndex: "address",
			key: 'address',
			render: (address) => address[0].name,
		},
		{
			title: "Город",
			dataIndex: "city",
			key: 'city',
		},
		{
			title: "Директор",
			dataIndex: "director_full_fio",
			key: 'director_full_fio',
		},
		{
			title: "Главбух",
			dataIndex: "buhgalter",
			key: 'buhgalter',
		},
		{
			title: "ИНН",
			dataIndex: "inn",
			key: 'inn',
		},
		{
			title: "КПП",
			dataIndex: "kpp",
			key: 'kpp',
		},
		{
			title: "ОГРН",
			dataIndex: "ogrn",
			key: 'ogrn',
		},
		{
			title: "БИК",
			dataIndex: "bik",
			key: 'bik',
		},
		{
			title: "Банк компании",
			dataIndex: "bank_name",
			key: 'bank_name',
		},
		// {
		// 	title: "Расч. счёт",
		// 	dataIndex: "bank_account_number",
		// 	key: 'bank_account_number',
		// },
		{
			title: "Действия",
			key: "action",
			render: (text) => {
				const { id } = text;

				return (
					<div className="d-flex gap-3 users justify-content-center">
						<ul className="list-inline font-size-20 contact-links mb-0">
							{/* редактировать адрес */}
							<li className="list-inline-item text-primary cursor-pointer"
								onClick={() => { handleCellClick(id) }}>
								<EditIcon id={`editicon-${id}`} />
							</li>
						</ul>
					</div>
				)
			},
		},
	],
		[handleCellClick]
	)

	return (
		<div className="page-content">
			<div className="container-fluid">
				<div className="d-flex align-items-center justify-content-between mb-2">
					<CustomRadioCompanies />
					<PrimaryBtn onClick={handleAddCompany}>
						Создать компанию
					</PrimaryBtn>
				</div>
				<Card>
					{loading && <Loader />}
					{!loading && companies && companies?.length > 0 && (
						<Table
							rowKey={(row) => row?.id}
							columns={columns}
							dataSource={companies}
							pagination={false}
							size='small'
						/>
					)}
				</Card>

				<Modal
					title={isEdit ? "Изменить компанию" : "Создать компанию"}
					open={modal}
					onCancel={cancelFormFields}
					width={720}
					footer={null}
				>
					<Form
						fields={fields}
						onFinish={onFinish}
						autoComplete="off"
						form={form}
					>
						<Row gutter={16}>
							<Col span={11}>
								<label className="form-label archi-label me-1">Сокращенное имя компании</label>
								<StarIcon />
								<Form.Item
									name="name"
									rules={[
										{
											required: true,
											message: 'Пожалуйста, введите имя компании!',
										},
									]}
								>
									<Input />
								</Form.Item>
							</Col>
							<Col span={8}>
								<label className="form-label archi-label">Лейбл для компании</label>
								<Form.Item
									name="label"
								>
									<Input />
								</Form.Item>
							</Col>
							<Col span={5}>
								<label className="form-label archi-label">Тип компании</label>
								<Select
									defaultValue={selectedTypeCompany}
									value={selectedTypeCompany}
									style={{
										width: '100%',
									}}
									onChange={handleTypeCompanyChange}
									options={[
										{
											value: 'Собственная',
											label: 'Собственная',
										},
										{
											value: 'Внешняя',
											label: 'Внешняя',
										},
									]}
								/>
							</Col>
						</Row>
						<Row>
							<Col span={24}>
								<label className="form-label archi-label me-1">Полное имя компании</label>
								<StarIcon />
								<Form.Item
									name="long_main_name"
									rules={[
										{
											required: true,
											message: 'Пожалуйста, введите полное имя компании!',
										},
									]}
								>
									<Input />
								</Form.Item>
							</Col>
						</Row>
						<Row >
							<Col span={24}>
								<label className="form-label archi-label me-1">Полное имя компании (род. падеж. Чего?)</label>
								<StarIcon />
								<Form.Item
									name="long_name"

									rules={[
										{
											required: true,
											message: 'Пожалуйста, введите полное имя компании в род. падеже!',
										},
									]}
								>
									<Input />
								</Form.Item>
							</Col>
						</Row>
						<Row gutter={16}>
							<Col span={7}>
								<label className="form-label archi-label">Город</label>
								<Form.Item
									name="city"

								>
									<Input />
								</Form.Item>
							</Col>
							<Col span={5}>
								<label className="form-label archi-label">Телефон</label>
								<Form.Item
									name="phone"

								>
									<Input />
								</Form.Item>
							</Col>
							<Col span={12}>
								<label className="form-label archi-label me-1">ФИО директора (инициалы)</label>
								<StarIcon />
								<Form.Item
									name="director"

									rules={[
										{
											required: true,
											message: 'Введите ФИО директора!',
										},
									]}
								>
									<Input />
								</Form.Item>
							</Col>
						</Row>
						<Row gutter={16}>
							<Col span={12}>
								<label className="form-label archi-label">Полное ФИО директора</label>
								<Form.Item
									name="director_full_fio"

								>
									<Input />
								</Form.Item>
							</Col>
							<Col span={12}>
								<label className="form-label archi-label">Полное ФИО директора (род. падеж. Кого?)</label>
								<Form.Item
									name="director_full_fio_rod_padeg"

								>
									<Input />
								</Form.Item>
							</Col>

						</Row>
						<Row gutter={16}>
							<Col span={12}>
								<label className="form-label archi-label">Полное ФИО директора (дат. падеж. Кому?)</label>
								<Form.Item
									name="director_full_fio_dat_padeg"

								>
									<Input />
								</Form.Item>
							</Col>
							<Col span={12}>
								<label className="form-label archi-label me-1">ФИО Главбуха (инициалы)</label>
								<StarIcon />
								<Form.Item
									name="buhgalter"

									rules={[
										{
											required: true,
											message: 'Введите ФИО Главбуха!',
										},
									]}
								>
									<Input />
								</Form.Item>
							</Col>
						</Row>
						<Row gutter={16}>
							<Col span={6}>
								<label className="form-label archi-label me-1">ИНН</label>
								<StarIcon />
								<Form.Item
									name="inn"

									rules={[
										{
											required: true,
											message: 'Введите ИНН!',
										},
									]}
								>
									<Input />
								</Form.Item>
							</Col>
							<Col span={6}>
								<label className="form-label archi-label me-1">КПП</label>
								<Form.Item
									name="kpp"
								>
									<Input />
								</Form.Item>
							</Col>
							<Col span={6}>
								<label className="form-label archi-label me-1">ОГРН (ОГРНИП)</label>
								<StarIcon />
								<Form.Item
									name="ogrn"

									rules={[
										{
											required: true,
											message: 'Введите ОГРН!',
										},
									]}
								>
									<Input />
								</Form.Item>
							</Col>
							<Col span={6}>
								<label className="form-label archi-label me-1">БИК</label>
								<StarIcon />
								<Form.Item
									name="bik"

									rules={[
										{
											required: true,
											message: 'Введите БИК!',
										},
									]}
								>
									<Input />
								</Form.Item>
							</Col>
						</Row>
						<Row gutter={16}>
							<Col span={12}>
								<label className="form-label archi-label me-1">Имя банка</label>
								<StarIcon />
								<Form.Item
									name="bank_name"

									rules={[
										{
											required: true,
											message: 'Введите имя банка!',
										},
									]}
								>
									<Input />
								</Form.Item>
							</Col>
							<Col span={12}>
								<label className="form-label archi-label me-1">Расчетный счет</label>
								<StarIcon />
								<Form.Item
									name="bank_account_number"

									rules={[
										{
											required: true,
											message: 'Введите расчетный счет!',
										},
									]}
								>
									<Input />
								</Form.Item>
							</Col>
						</Row>
						<Row gutter={16}>
							<Col span={12}>
								<label className="form-label archi-label me-1">Корпоративный счет</label>
								<StarIcon />
								<Form.Item
									name="bank_account_number_corporate"

									rules={[
										{
											required: true,
											message: 'Введите корпоративный счет!',
										},
									]}
								>
									<Input />
								</Form.Item>
							</Col>
							<Col span={12}>
								<label className="form-label archi-label">ОКВЭД</label>
								<Form.Item
									name="okved"

								>
									<Input />
								</Form.Item>
							</Col>
						</Row>
						<Row className="mb-3">
							<label className="form-label archi-label">Юр. адрес | реквизиты документов</label>
							<ArchiSelect
								options={companyAddressData}
								value={selectedCompanyAddress}
								onChange={handleSelectedCompanyAddress}
								className="main__select"
								classNamePrefix="main__select"
								placeholder={'Выбрать'}
							/>
						</Row>
						<Row className="mb-3">
							<label className="form-label archi-label">Фактический адрес | Почтовый адрес в реквизитах</label>
							<ArchiSelect
								options={companyFactAddressData}
								value={selectedFactAddress}
								onChange={handleSelectedFactAddress}
								className="main__select"
								classNamePrefix="main__select"
								placeholder={'Выбрать'}
							/>
						</Row>
						<Row className="mb-3">
							<label className="form-label archi-label">Адрес для поля 1.2 в уведомлении</label>
							<ArchiSelect
								options={companyAddress12Data}
								value={selectedAddress12}
								onChange={handleSelectedAddress12}
								className="main__select"
								classNamePrefix="main__select"
								placeholder={'Выбрать'}
							/>
						</Row>

						<Row className="mb-3">
							<label className="form-label archi-label">Адрес для поля 3.4 в уведомлении</label>
							<ArchiSelect
								options={companyAddress34Data}
								value={selectedAddress34}
								onChange={handleSelectedAddress34}
								className="main__select"
								classNamePrefix="main__select"
								placeholder={'Выбрать'}
							/>
						</Row>

						<Row className="mb-3">
							<label className="form-label archi-label">Место подачи в уведомлении</label>
							<ArchiSelect
								options={companyFilingAddressData}
								value={selectedFilingAddress}
								onChange={handleSelectedFilingAddress}
								className="main__select"
								classNamePrefix="main__select"
								placeholder={'Выбрать'}
							/>
						</Row>

						<Row className="justify-content-end">
							<div className="text-end">
								<Form.Item>
									{isSuperUser && <Button type="primary" htmlType="submit">
										{isEdit ? 'Обновить компанию' : 'Сохранить компанию'}
									</Button>}
								</Form.Item>
							</div>
						</Row>
					</Form>
				</Modal>
			</div>
			<Toaster position="top-right"
				reverseOrder={true} />
		</div>
	);
};

export default Companies;